<template>
  <video-js ref="hlsPlayer" class="vjs-default-skin hls-player" data-setup="{}">
  </video-js>
</template>

<script>
import videojs from "video.js";
import { equalRounded } from "@/utils/calc.js";
import { mapGetters } from "vuex";

// @group Media/Video
// HLS video player with multiple playback modes
export default {
  name: "HlsPlayer",
  /** rewind step timeout (in milliseconds) */
  rewindInterval: 1000,
  /** rewind step time (in seconds) */
  rewindStep: 2,
  /** fast mode speed */
  fastMultiplier: 2,
  /** time between two reloading attempts in ms */
  reloadStreamInterval: 1000,
  /** if difference between internal and set time more than this, then start synchronizing */
  syncThreshold: 1,
  internalSyncThreshold: 3,
  props: {
    time: {
      type: Number,
      required: true,
      validator(val) {
        return Number.isFinite(val) && val >= 0;
      }
    },
    /** set video link */
    link: {
      type: String,
      required: true
    },
    /** player current time in seconds */
    playerTime: {
      type: Number,
      default: 0,
      validator(val) {
        return Number.isFinite(val) && val >= 0;
      }
    },
    /** pause/play video */
    paused: {
      type: Boolean,
      default: false
    },
    /** set sound volume */
    volume: {
      type: Number,
      default: 1.0,
      validator(vol) {
        return vol >= 0 && vol <= 1;
      }
    },
    /** mute/unmute video */
    muted: {
      type: Boolean,
      default: false
    },
    /** playback mode */
    mode: {
      type: String,
      default: "normal",
      validator(mode) {
        const availableModes = ["rewind", "normal", "fast"];
        return availableModes.includes(mode);
      }
    },

    /** video poster */
    poster: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      /** Hls.js management object */
      hls: null,
      hasNativeHls: false,
      isLoaded: false,
      /** is player start to play video */
      isPlaying: false,
      /** rewind setInterval ID to stop it */
      rewindId: null,
      /** we can change layer time only when it's true (on Safari) */
      canToMove: true,
      /** VideoJS player object */
      player: null
    };
  },
  computed: {
    ...mapGetters("thumbnails", ["getThumbnailLink"]),
    // @vuese
    // Is player playing backwards
    rewind() {
      return this.mode == "rewind";
    },
    // @vuese
    // Is player in fast mode
    fastForward() {
      return this.mode == "fast";
    },
    previewImage() {
      if(this.getThumbnailLink(this.time)){
        return this.getThumbnailLink(this.time);
      } else {
        return this.poster
      }
    },
    imageSrc() {
      return this.player.cache_.source.src;
    }
  },

  watch: {
    // @vuese
    // Synchronize player time
    playerTime() {
      this.syncPlayerTime();
    },
    // @vuese
    // Reload link
    link() {
      if (this.link) {
        this.load(this.link);
        this.player.poster(this.previewImage);
      }
    },
    // @vuese
    // Change poster
    poster() {
        this.player.poster(this.previewImage);
    },
    // @vuese
    // Play/pause video
    paused() {
      if (this.isLoaded) {
        this.syncPaused();
        this.player.poster(this.previewImage);
      }
    },
    // @vuese
    // Change volume
    volume() {
      this.setVolume(this.volume);
    },
    // @vuese
    // Mute/unmute
    muted() {
      if (this.muted) {
        this.mute();
      } else {
        this.unmute();
      }
    },
    // @vuese
    // Synchronize playback mode
    mode(mode, prev) {
      this.syncPlaybackMode(prev);
    },
    // @vuese
    // Call external handler playing/played
    isPlaying() {
      if (this.isPlaying) {
        /** player is trying to start */
        this.$emit("playing");
      } else {
        /** player is played or paused now */
        this.$emit("played");
      }
    },
    // @vuese
    // Restart player after loading (if it's needed) or block player time setting before it
    isLoaded() {
      if (this.isLoaded) {
        this.syncPaused();
        this.syncPlaybackMode();
        /** player loaded new source and has duration ($event = { duration }) */
        this.$emit("loaded", { duration: this.player.duration() });
      } else {
        this.canToMove = false;
        /** player started loading new source */
        this.$emit("loading");
        this.player.poster(this.poster)
      }
    }
  },
  mounted() {
    const player = this.getPlayerOrThrow();
    this.player = videojs(player, {
      fluid: true,
      html5: {
        hls: {
          overrideNative: true
        },
        nativeAudioTracks: false,
        nativeVideoTracks: false
      }
    });

    this.player.crossorigin("anonymous");
    this.player.poster(this.poster);
    this.player.poster(this.previewImage);

    /** data sync */
    this.setVolume(this.volume);

    /** synchronize mute */
    if (this.muted) {
      this.mute();
    } else {
      this.unmute();
    }

    /** load source */
    if (this.link) {
      this.load(this.link);
    }

    /** set playback mode */
    this.syncPlaybackMode();

    const video = this.$refs.hlsPlayer.getElementsByTagName("video")[0];
    /** player loaded */
    this.$emit("playerRendered", video);

    /** update time when it changed */
    this.player.on("timeupdate", () => {
      this.$emit("timeChanged", this.player.currentTime());
    });

    /** player loaded source */
    this.player.on("durationchange", () => {
      this.isLoaded = true;
    });

    /** first frame loaded/emptied listeners */
    this.player.on("emptied", () => {
      /** frame has disappeared (and can't be get to snapshot) */
      this.$emit("imageEmptied");
    });
    this.player.on("loadeddata", () => {
      /** frame has appeared (and can be get to snapshot) */
      this.$emit("imageLoaded");
    });

    this.player.on("error", () => {
      this.handleStreamFailed(this.player.error());
    });
  },
  methods: {
    // @vuese
    // Adjust player's play/paused to 'paused' property.
    // Player can be paused or muted during the method's execution
    async syncPaused() {
      /** synchronizing must be executed only for ready player */
      if (!this.isLoaded) {
        /** sync when player will load */
        const unwatchLoaded = this.$watch("isLoaded", () => {
          this.syncPaused();
          unwatchLoaded();
        });
        console.warn(
            "Player cannot be play/paused when stream is not fully loaded"
        );
        return;
      }

      /** synchronized must be executed only for play/paused video state */
      if (this.isPlaying) {
        /** sync when player will played */
        const unwatchPlaying = this.$watch("isPlaying", () => {
          this.syncPaused();
          unwatchPlaying();
        });
        console.warn("Player just started to play");
        return;
      }

      if (this.paused) {
        this.pause();
      } else {
        /** set playing flag to prevent accidential changes */
        this.isPlaying = true;

        /** try to play video (mute it if it's needed) */
        try {
          await this.playWithPromise();
        } catch (err) {
          /** playeing has failed, so we try to mute it and play again */
          if (!this.muted) {
            this.$emit("update:muted", true);

            /** wait when mute will be apllied */
            await this.$nextTick();

            try {
              await this.playWithPromise();
            } catch (err) {
              /** playing has failed, so it's need to be paused */
              this.$emit("update:paused", true);
              console.error(err);
            }
          } else {
            /** playing has failed, so it's need to be paused */
            this.$emit("update:paused", true);
            console.error(err);
          }
        } finally {
          /** playing will be finished anyway */
          this.isPlaying = false;
        }
      }
    },
    // @vuese
    // Adjust playback to 'mode' property.
    syncPlaybackMode(previousMode) {
      /** synchronizing must be executed only for ready player */
      if (!this.isLoaded) {
        const unwatchLoaded = this.$watch("isLoaded", () => {
          this.syncPlaybackMode(previousMode);
          unwatchLoaded();
        });
        return;
        // throw new Error("Player cannot be forwarded/rewinded when stream is not fully loaded");
      }

      /** synchronized must be executed only for play/paused video state */
      if (this.isPlaying) {
        const unwatchPlaying = this.$watch("isPlaying", () => {
          this.syncPlaybackMode(previousMode);
          unwatchPlaying();
        });
        return;
        // throw new Error("Player is changing it playing status");
      }

      switch (previousMode) {
        case "rewind":
          if (!this.paused) {
            /** if previuous mode was 'rewind' then we need to
             * cancel it and start player becuase rewind is working
             * with stopped player
             **/
            this.stopRewind();
            this.startPlayerWithPromise();
          }
          break;
        case "fast":
          this.stopFast();
          break;
      }

      switch (this.mode) {
        case "rewind":
          /** when player paused rewind isn't working, so it doesn't need to stop it */
          if (!this.paused) {
            this.initRewind();
          }
          break;
        case "fast":
          this.startFast();
          break;
      }
    },
    // @vuese
    // Adjust player.currentTime to 'playerTime' property.
    syncPlayerTime() {
      const diff = Math.abs(this.playerTime - this.player.currentTime());

      if (diff >= this.$options.syncThreshold) {
        this.player.currentTime(this.playerTime);

        /** (for Safari): if time set is failed then set it when it's possible */
        const actualTime = this.player.currentTime();
        const expectedTime = this.playerTime;
        if (!equalRounded(expectedTime, actualTime)) {
          this.player.one("canplay", () => {
            this.player.currentTime(expectedTime);
          });
        }
      }
    },
    // @vuese
    // Return <video> object or throw Error if it doesn't exist
    getPlayerOrThrow() {
      const player = this.$refs.hlsPlayer;
      if (!player) {
        throw new Error("Player isn't rendered yet");
      }

      return player;
    },
    // @vuese
    // Start player with corresponding playback mode, return Promise
    startPlayerWithPromise() {
      if (this.mode !== "rewind") {
        return this.playWithPromise();
      } else {
        /** start rewinding */
        this.initRewind();
        return Promise.resolve();
      }
    },
    // @vuese
    // Stop playing or backward rewinding
    stopPlayer() {
      if (this.mode !== "rewind") {
        this.pause();
      } else {
        this.stopRewind();
      }
    },
    // @vuese
    // Start backward rewinding process and stop player to prevent bad playing
    initRewind() {
      this.pause();
      this.startRewind();
    },
    // @vuese
    // Start to play backwards
    startRewind() {
      if (!this.rewindId) {
        this.rewindId = setInterval(
            this.stepRewind,
            this.$options.rewindInterval
        );
      }
    },
    // @vuese
    // Step to back one time
    stepRewind() {
      const time = this.player.currentTime();
      if (time > this.$options.rewindStep) {
        this.player.currentTime(time - this.$options.rewindStep);
      } else if (time > 0) {
        this.player.currentTime(0);
      }
    },
    // @vuese
    // Stop to play backwards
    stopRewind() {
      if (this.rewindId) {
        clearInterval(this.rewindId);
        this.rewindId = null;
      }
    },
    // @vuese
    // Start to play fast
    startFast() {
      this.player.playbackRate(this.$options.fastMultiplier);
    },
    // @vuese
    // Stop to play fast
    stopFast() {
      this.player.playbackRate(1.0);
    },
    // @vuese
    // Try to start player and return result as a play() Promise
    playWithPromise() {
      return this.player.play();
    },
    // @vuese
    // Stop player
    pause() {
      this.player.pause();
    },
    // @vuese
    // Change player volume
    // @arg val - new volume
    setVolume(val) {
      this.player.volume(val);
    },
    // @vuese
    // Mute player
    mute() {
      this.player.muted(true);
    },
    // @vuese
    // Unmute player
    unmute() {
      this.player.muted(false);
    },
    // @vuese
    // Reload source
    load(source) {
      this.isLoaded = false;

      if (this.hls) {
        this.hls.destroy();
      }

      this.player.src({
        src: source,
        type: "application/x-mpegURL"
      });
      // if (this.hasNativeHls){
      //   /** Use native player on Safari */
      //   player.src = source;
      //   player.addEventListener("error", err => {
      //     this.handleStreamFailed({
      //       type: "none",
      //       details: "none",
      //       fatal: true
      //     });
      //   });
      // } else if (Hls.isSupported()) {
      //   /** HLS initializing */
      //   this.hls = new Hls();

      //   this.hls.attachMedia(player);

      //   this.hls.on(Hls.Events.MEDIA_ATTACHED, () => {
      //     this.hls.loadSource(source);
      //   });

      //   this.hls.on(Hls.Events.ERROR, (error, data) => {
      //     this.handleStreamFailed(data);
      //   });
      // } else {
      //   /** fatal error */
      //   this.$emit("error", "HLS not supported");
      // }
    },
    // @vuese
    // Handle stream HLS error
    handleStreamFailed(error) {
      /** Reload stream only when it won't be reloaded itself */
      console.error(`Stream ${this.link} was failed and will be reloaded`);
      console.log(error);
      setTimeout(
          () => this.load(this.link),
          this.$options.reloadStreamInterval
      );
    }
  }
};
</script>

